
import React, { useState } from 'react';
import { Page } from '../types';
import { SITE_CONTENT } from '../constants/content';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, TouchSensor, DragEndEvent } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';


interface SortableItemProps {
  id: string;
  index: number;
  isOrderingCorrect: boolean | null;
}

const SortableItem: React.FC<SortableItemProps> = ({ id, index, isOrderingCorrect }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging
  } = useSortable({ id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 1,
    touchAction: 'none',
  };

  const getStatusClasses = () => {
    if (isOrderingCorrect === null) return 'bg-white border-blue-500 shadow-md cursor-grab active:cursor-grabbing';
    return isOrderingCorrect ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500';
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`w-full p-4 rounded-2xl border-2 text-[#003249] font-bold text-sm text-left flex items-center gap-4 transition-shadow ${getStatusClasses()} ${isDragging ? 'opacity-50 shadow-2xl' : ''}`}
    >
      <span className="w-6 h-6 rounded-full bg-blue-500 text-white flex items-center justify-center text-[10px] shrink-0">{index + 1}</span>
      <span className="flex-1">{id}</span>
      {isOrderingCorrect === null && (
          <i className="fa-solid fa-grip-vertical text-gray-300"></i>
      )}
    </div>
  );
};


interface HeroProps {
  navigate: (page: Page) => void;
}

const HeroLessonContent = {
  steps: [
    {
      type: "textblock",
      text: "Der Notruf ist das erste und wichtigste Glied in der Rettungskette. Ein guter Notruf spart wertvolle Minuten. Am anderen Ende der Leitung sitzt ein Profi (Disponent), der genau weiß, welche Infos er braucht. Deine Aufgabe ist es, seine Fragen bestmöglich zu beantworten."
    },
    {
      type: "keyfacts",
      facts: [
        {title: "Die 5 W-Fragen", text: "Die Struktur des Gesprächs folgt meist den 5 Ws: Wo? Was? Wie viele? Welche? Warten!", icon: "fa-question-circle"},
        {title: "Europaweit gültig", text: "Die 112 ist die kostenlose Notrufnummer in der gesamten Europäischen Union für Feuerwehr und Rettungsdienst.", icon: "fa-globe-europe"},
      ]
    },
    {
      type: "ordering",
      description: "Ein guter Notruf ist gut strukturiert. Bringe die 5 W-Fragen in die richtige Reihenfolge.",
      items: [
        "Wo ist es passiert?",
        "Was ist geschehen?",
        "Wie viele Verletzte gibt es?",
        "Welche Art von Verletzungen?",
        "Warten auf Rückfragen."
      ],
    },
    {
      type: "quiz",
      question: "Welche W-Frage ist die allerwichtigste, falls die Verbindung abbricht?",
      correctIdx: 0,
      explanation: "Die 'Wo'-Frage ist entscheidend. Selbst wenn die Verbindung abbricht, kann die Leitstelle bereits Hilfe zum richtigen Ort schicken.",
      options: ["Wo?", "Was?", "Wer?"],
    },
    {
      type: "textblock",
      text: "Lust auf mehr? Du kannst dich jetzt für unseren Newsletter anmelden und bekommst als erstes bescheid, wenn es etwas neues zu wissen gibt!"
    },
  ]
}

const Hero: React.FC<HeroProps> = ({ navigate }) => {
  const [currentStep, setCurrentStep] = useState<number>(0);
  const [isCorrect, setIsCorrect] = useState<boolean | null>(null);
  const [selectedOption, setSelectedOption] = useState<number | null>(null)
  const [orderedItems, setOrderedItems] = useState<string[]>([...(HeroLessonContent.steps[2].items || [])].sort(() => Math.random() - 0.5));
  const [isOrderingCorrect, setIsOrderingCorrect] = useState<boolean | null>(null);
  const progressPercent = (currentStep / (HeroLessonContent.steps.length-1)) * 100;
  const feedbackText = HeroLessonContent.steps[3].explanation;

  const { hero } = SITE_CONTENT.home;

  function handleNext() {
    setIsCorrect(null);
    setCurrentStep(currentStep+1);
  }

  const handleOptionSelect = (index: number) => {
    if (isCorrect !== null) return;
    setSelectedOption(index);
    const q = HeroLessonContent.steps[currentStep];
    const correct = index === q.correctIdx;
    setIsCorrect(correct);
  };

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setOrderedItems((items) => {
        const oldIndex = items.indexOf(active.id as string);
        const newIndex = items.indexOf(over.id as string);
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const checkOrdering = () => {
    const currentStep_ordering = HeroLessonContent.steps[currentStep];
    if (!currentStep_ordering?.items) return;
    
    const correct = JSON.stringify(orderedItems) === JSON.stringify(currentStep_ordering.items);
    setIsOrderingCorrect(correct);
    setIsCorrect(correct);
  };

  return (
    <div className="pt-32 pb-20 px-4 md:px-6 relative overflow-hidden">
      <div className="absolute top-0 right-0 w-96 h-96 bg-primary opacity-5 rounded-full -mr-48 -mt-24 blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-secondary opacity-5 rounded-full -ml-32 -mb-16 blur-3xl"></div>

      <div className="container mx-auto flex flex-col md:flex-row items-center justify-between gap-12 relative z-10">
        <div className="flex-1 text-center md:text-left">
          <span className="inline-flex items-center bg-primary/10 text-primary px-4 py-2 rounded-full text-xs font-bold mb-6">
            {hero.badge}
          </span>
          <h1 className="text-5xl md:text-7xl font-extrabold text-secondary leading-tight mb-6">
            {hero.headline} <br />
            <span className="text-primary italic font-black text-4xl md:text-6xl">{hero.headlineItalic}</span>
          </h1>
          <p className="text-xl text-gray-600 mb-10 max-w-xl mx-auto md:mx-0 leading-relaxed">
            {hero.description}
          </p>
          
          <div className="flex flex-col gap-6 md:gap-4 justify-center md:justify-start">
            <div className="flex flex-col gap-4 sm:flex-row justify-center md:justify-start items-center">
              <button 
                onClick={() => navigate('warteliste')}
                className="w-full sm:w-auto bg-primary text-white px-10 py-5 rounded-2xl font-black text-xl hover:scale-105 transition-all shadow-2xl shadow-primary/30 active:scale-95 min-h-[48px]"
              >
                {hero.ctaPrimary}
              </button>
              <button 
                onClick={() => {
                  const el = document.getElementById('warum');
                  el?.scrollIntoView({ behavior: 'smooth' });
                }}
                className="w-full sm:w-auto bg-white border-2 border-secondary text-secondary px-8 py-5 rounded-2xl font-bold text-lg hover:bg-gray-50 transition-all active:scale-95 min-h-[48px]"
              >
                {hero.ctaSecondary}
              </button>
            </div>
            
            <div className="flex flex-wrap justify-center md:justify-start gap-x-4 gap-y-2">
              {hero.microcopy.map((text, i) => (
                <span key={i} className="text-xs font-bold text-gray-400 uppercase tracking-widest flex items-center">
                  {text}
                </span>
              ))}
            </div>
          </div>

          <div className="mt-12 hidden md:block">
            <p className="text-sm text-gray-400 italic">
              Entwickelt mit Fokus auf verständliches Erste-Hilfe-Wissen für den Alltag.
            </p>
          </div>
        </div>

          <div className="flex-1 relative max-w-md hidden md:block">
            <div className="relative mx-auto border-secondary bg-secondary border-[14px] rounded-[3rem] h-[694px] w-[350px] shadow-2xl overflow-visible">
              <div className="rounded-[2.2rem] overflow-hidden w-full h-full bg-white flex flex-col relative">


                <div className="inset-0 bg-white z-50 flex flex-col overflow-hidden">
                  <div className="px-6 py-8 flex items-center gap-4">
                    <button onClick={() => {setCurrentStep(0); setIsCorrect(null); setSelectedOption(null); setIsOrderingCorrect(null); setOrderedItems([...(HeroLessonContent.steps[2].items || [])].sort(() => Math.random() - 0.5))}} className="text-gray-400 hover:text-[#ee223b] transition-colors text-3xl"><i className="fa-solid fa-xmark"></i></button>
                    <div className="flex-1 relative">
                    <div className="h-5 bg-gray-100 rounded-full overflow-hidden shadow-inner border border-gray-50 p-1">
                      <div className="h-full bg-[#58cc02] transition-all duration-700 ease-out rounded-full relative group shadow-[inset_0_2px_4px_rgba(255,255,255,0.3)]" style={{ width: `${progressPercent}%` }}>
                      <div className="absolute top-0 left-0 right-0 h-1/2 bg-white/20 rounded-full" />
                      </div>
                    </div>
                    </div>
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-red-50 rounded-xl border border-red-100 shadow-sm">
                    <i className="fa-solid fa-heart text-red-500 text-sm"></i>
                    <span className="font-black text-[#003249] text-sm">∞</span>
                    </div>
                  </div>
                </div>

                <div className="flex-1 overflow-y-auto px-2 pb-40" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                  <div className="max-w-xl mx-auto">

                    {HeroLessonContent.steps[currentStep]?.type == 'textblock' && (
                      <>
                        <div className="w-16 h-16 bg-blue-50 text-[#185ad6] rounded-2xl flex items-center justify-center text-2xl mb-6">
                          <i className="fa-solid fa-circle-info"></i>
                        </div>
                        <div className="bg-gray-50 p-8 rounded-[2.5rem] border-2 border-gray-100 shadow-inner">
                          <div className="text-l text-[#003249] font-medium leading-relaxed" dangerouslySetInnerHTML={{ __html: HeroLessonContent.steps[currentStep].text || '' }} />
                        </div>
                      </>
                    )}

                    {HeroLessonContent.steps[currentStep]?.type == 'keyfacts' && (
                      <div className="animate-pop-in">
                        <h3 className="text-2xl font-black text-[#003249] mb-6">Wichtige Fakten</h3>
                        <div className="grid grid-cols-1 gap-4">
                            {HeroLessonContent.steps[currentStep].facts?.map((fact: {icon: string, title: string, text: string}, i: number) => (
                            <div key={i} className="flex gap-4 p-6 bg-white rounded-3xl border-2 border-gray-100 shadow-sm">
                              <div className="w-12 h-12 bg-blue-50 rounded-2xl flex items-center justify-center text-blue-500 text-xl shrink-0">
                                <i className={`fa-solid ${fact.icon}`}></i>
                              </div>
                              <div>
                                <h4 className="font-black text-[#003249]" dangerouslySetInnerHTML={{ __html: fact.title }} />
                                <p className="text-sm text-gray-500 font-medium" dangerouslySetInnerHTML={{ __html: fact.text }} />
                              </div>
                            </div>
                            ))}
                        </div>
                      </div>
                    )}

                    {HeroLessonContent.steps[currentStep]?.type == 'quiz' && (
                      <div className="animate-pop-in">
                      <div className="flex justify-between items-start mb-8">
                        <h3 className="text-2xl font-black text-[#003249] flex-1 pr-4">{HeroLessonContent.steps[currentStep].question}</h3>
                      </div>
                      <div className="space-y-4">
                          {HeroLessonContent.steps[currentStep].options?.map((opt, idx) => (
                          <button key={idx} onClick={() => handleOptionSelect(idx)} disabled={isCorrect !== null} className={`w-full p-6 text-left rounded-3xl border-2 transition-all font-bold text-lg flex items-center gap-4 ${isCorrect === null ? 'border-gray-100 hover:border-gray-200 duo-shadow' : (idx === HeroLessonContent.steps[currentStep].correctIdx ? 'border-green-500 bg-green-50 text-green-700' : (idx === selectedOption ? 'border-red-500 bg-red-50 text-red-700' : 'opacity-40 grayscale'))}`}>
                            <span className="w-8 h-8 rounded-lg bg-gray-50 border border-gray-100 flex items-center justify-center text-xs shrink-0">{idx + 1}</span>
                            {opt}
                          </button>
                          ))}
                      </div>
                      </div>
                    )}

                    {HeroLessonContent.steps[currentStep]?.type === 'ordering' && (
                      <>
                        <h3 className="text-2xl font-black text-[#003249] mb-2">{HeroLessonContent.steps[currentStep].description}</h3>
                        <p className="text-gray-400 text-sm mb-8 font-medium italic">Halte ein Element gedrückt, um es zu verschieben.</p>
                        
                        <div className="overflow-hidden">
                          <DndContext 
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={handleDragEnd}
                          >
                            <SortableContext 
                              items={orderedItems}
                              strategy={verticalListSortingStrategy}
                            >
                              <div className="space-y-3">
                                {orderedItems.map((item, idx) => (
                                  <SortableItem 
                                    key={item} 
                                    id={item} 
                                    index={idx} 
                                    isOrderingCorrect={isOrderingCorrect} 
                                  />
                                ))}
                              </div>
                            </SortableContext>
                          </DndContext>
                        </div>
                      </>
                    )}

                  </div>
                </div>

                <div className={`absolute bottom-0 left-0 right-0 p-6 border-t-2 transition-all duration-300 ${isCorrect === null ? 'bg-white border-gray-200' : (isCorrect ? 'bg-green-100 border-green-200' : 'bg-red-100 border-red-200')}`}>
                  <div className="max-w-xl mx-auto">
                    {isCorrect !== null && (
                      <div className="mb-6 animate-pop-in">
                      <div className="flex items-center gap-3 mb-2">
                        <i className={`fa-solid ${isCorrect ? 'fa-circle-check text-green-600' : 'fa-circle-xmark text-red-600'} text-2xl`}></i>
                        <h4 className="text-xl font-black">{isCorrect ? 'Richtig!' : 'Nicht ganz richtig...'}</h4>
                      </div>
                      <p className="text-sm font-medium opacity-80 leading-snug">{feedbackText}</p>
                      </div>
                    )}
                    <button onClick={currentStep >= HeroLessonContent.steps.length - 1 ? () => navigate('warteliste') : (HeroLessonContent.steps[currentStep]?.type === 'ordering' && isOrderingCorrect === null ? checkOrdering : handleNext)} disabled={(HeroLessonContent.steps[currentStep]?.type == 'quiz' && selectedOption === null) && currentStep < HeroLessonContent.steps.length-1} className={`w-full py-5 rounded-2xl font-black text-xl text-white shadow-lg tracking-widest transition-all active:translate-y-1 ${isCorrect === null ? 'bg-[#ee223b] shadow-[0_5px_0_0_#c41a2f]' : (isCorrect ? 'bg-green-500 shadow-[0_5px_0_0_#22863a]' : 'bg-red-500 shadow-[0_5px_0_0_#b91c1c]')}`}>
                      {currentStep >= HeroLessonContent.steps.length - 1 ? 'Zur Warteliste' : 'WEITER'}
                    </button>
                  </div>
                </div>

              </div>
           </div>
        </div>
      </div>
    </div>
  );
};

export default Hero;
